import { Box, Button, IconButton, useMediaQuery } from "@material-ui/core";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import { useDispatch, useSelector } from "react-redux";
import { uniqueId } from "lodash";
import { toast } from "react-toastify";
import { useMemo } from "react";

import { useSummaryStyles } from "./summary.styles";
import { RootState } from "../../../../app/rootReducer";
import { paymentTypeTitle, quateAddonQuantityValue, quoteAvailableSubscriptions } from "../../../../app/utils";
import { QuoteLineItem } from "../../../../api/Quote/QuoteLineItem";
import { changeQuantityAddonQuote, removeAddonQuote, removeSubscriptionQuote } from "../../../../store/QuoteSlice";
import { AddonForm } from "../../../../api/Addon/AddonApi";
import { EEventType, eventAnalytic } from "../../../../app/analytics";
import { ProductSubscription, getFirstImage } from "../../../../api/Product/ProductSubscriptionApi";
import { SubscriptionDelayInDays } from "../../../../app/consts";
import { format } from "date-fns";
import { QuoteSubscriptionForm } from "../../../../api/QuoteSubscription/QuoteSubscriptionApi";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

export const SummaryAddon = ({ quoteId }: { quoteId: string }) => {
  const classes = useSummaryStyles();
  const matchesSmallMobile = useMediaQuery("(max-width:360px)");
  const dispatch = useDispatch();
  const { quote } = useSelector((state: RootState) => state.quote);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const onChangeQuantity = (addon: QuoteLineItem, quantity: number) => {
    dispatch(
      changeQuantityAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: quantity
      } as AddonForm)
    );
  };

  const handleRemove = (addon: QuoteLineItem) => {
    dispatch(
      removeAddonQuote(quoteId, {
        addonId: addon.productId
      } as AddonForm)
    );
    eventAnalytic.record(EEventType.RemovedAddon);
    toast.success(addon.name + " has been removed");
  };

  const handleRemoveSubscription = (subscription: ProductSubscription) => {
    dispatch(
      removeSubscriptionQuote(quoteId, {
        productId: subscription.id
      } as QuoteSubscriptionForm)
    );
    eventAnalytic.record(EEventType.RemovedSubscription);
    toast.success(subscription.name + " has been removed");
  };

  let date = new Date();
  date.setDate(date.getDate() + SubscriptionDelayInDays);
  const startDate = format(date, "do LLLL");

  const availableSubscriptions = useMemo(() => {
    if (subscriptions && quote) return quoteAvailableSubscriptions(quote, subscriptions);
    return [];
  }, [subscriptions, quote]);

  if (
    (!quote?.addons?.length || !quote.addons.filter((v) => v.selected).length) &&
    !(availableSubscriptions.length && quote?.subscriptions?.length)
  )
    return null;
  return (
    <Box className={classes.summaryAddon}>
      <h3 className={classes.summarySectionTitle}>Add-ons / Extra Service</h3>
      {!quote?.addons?.length || !quote.addons.filter((v) => v.selected).length
        ? null
        : quote.addons
            .filter((v) => v.selected)
            .map((item) => (
              <Box className={classes.summaryAddonProduct} key={uniqueId()}>
                <Box>
                  <Box className={classes.summaryAddonProductImage}>
                    <img src={item.imageUrl} alt="boiler" width={"100%"} />
                  </Box>
                </Box>
                <Box className={classes.summaryAddonProductDetail}>
                  <div className={classes.summaryAddonProductName}>{item.name}</div>
                  <Box className={classes.summaryAddonProductPrice} flexWrap={matchesSmallMobile ? "wrap" : "nowrap"}>
                    <Box className={classes.inputStepper}>
                      <IconButton
                        size="small"
                        disabled={item.quantity === 1}
                        onClick={() => {
                          onChangeQuantity(item, item.quantity - 1);
                        }}
                        className={classes.summaryAddonQuantityButton}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <input min="0" name="quantity" value={item.quantity} readOnly />
                      <IconButton
                        size="small"
                        disabled={item.quantity === item.maxQuantity}
                        onClick={() => {
                          onChangeQuantity(item, item.quantity + 1);
                        }}
                        className={classes.summaryAddonQuantityButton}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {!matchesSmallMobile && (
                      <Button
                        className={classes.summaryAddonProductButton}
                        onClick={() => {
                          handleRemove(item);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                  <Box className={classes.summaryAddonProductPrice}>
                    <PriceFormat value={quateAddonQuantityValue(item)} />
                    {matchesSmallMobile && (
                      <Button
                        className={classes.summaryAddonProductButton}
                        onClick={() => {
                          handleRemove(item);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}

      {!!availableSubscriptions.length &&
        availableSubscriptions.map((item) => {
          const selected = quote?.subscriptions?.some((i) => i.productId === item.id) ?? false;
          if (!selected) return null;
          return (
            <Box className={classes.summarySubscription} key={uniqueId()}>
              <Box>
                <Box className={classes.summarySubscriptionImage}>
                  <img src={getFirstImage(item)?.url} alt={item.name} width={"100%"} />
                </Box>
              </Box>

              <Box className={classes.summarySubscriptionDetail}>
                <div className={classes.summarySubscriptionName}>{item.name}</div>
                <div className={classes.summarySubscriptionDescription}>
                  We will send you confirmation once your subscription begins on <span>{startDate}</span>
                </div>
                <Box className={classes.summarySubscriptionPrice}>
                  <Box className={classes.summarySubscriptionPrice}>
                    <PriceFormat value={item.prices[0].price} />
                    &nbsp;{paymentTypeTitle(item.prices[0].paymentType, true)}
                  </Box>
                  <Button
                    className={classes.summarySubscriptionButton}
                    onClick={() => {
                      handleRemoveSubscription(item);
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
      <Box height={"1px"} bgcolor={"#979797"} width={"100%"} />
    </Box>
  );
};
